import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaUser, FaHistory, FaCreditCard, FaStar, FaUsers, FaTrash, FaEdit, FaEnvelope, FaBan, FaCheck, FaExclamationTriangle, FaEye, FaTimes, FaPlus, FaSort, FaSearch, FaClipboardList, FaMoneyBillWave, FaBriefcase } from 'react-icons/fa';
import Modal from '../components/Modal';
import FormField from '../components/FormField';
import ConfirmModal from '../components/ConfirmModal';
import MessageModal from '../components/MessageModal';
import { useClient, useClientEmployees, useClientActivity, useClientRequests, useClientPaiements, useClientEvaluations, useClientSignalements } from '../hooks/useClients';
import '../styles/ClientDetail.css';
import '../styles/ClientDetail-figma-isolated.css';

// Fonction utilitaire pour formater les dates
const getTimeAgo = (date) => {
  const now = new Date();
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  
  if (minutes < 60) return `il y a ${minutes}mn`;
  if (hours < 24) return `il y a ${hours}h`;
  if (days < 7) return `il y a ${days} jour${days > 1 ? 's' : ''}`;
  if (days < 30) return `il y a ${Math.floor(days / 7)} semaine${Math.floor(days / 7) > 1 ? 's' : ''}`;
  if (days < 365) return `il y a ${Math.floor(days / 30)} mois`;
  return `il y a ${Math.floor(days / 365)} an${Math.floor(days / 365) > 1 ? 's' : ''}`;
};

const ClientDetail = () => {
  console.log('🚀 ClientDetail - Composant chargé !');
  const { id } = useParams();
  const navigate = useNavigate();
  
  console.log('🔍 ClientDetail - ID reçu:', id);
  
  const { client: rawClient, loading, error } = useClient(id);
  const { employees, loading: employeesLoading, addEmployee, updateEmployee, deleteEmployee } = useClientEmployees(id);
  const { activities, loading: activitiesLoading } = useClientActivity(id);
  const { requests, loading: requestsLoading } = useClientRequests(id);
  const { paiements, loading: paiementsLoading } = useClientPaiements(id);
  const { evaluations, loading: evaluationsLoading } = useClientEvaluations(id);
  const { signalements, stats: signalementsStats, loading: signalementsLoading } = useClientSignalements(id);

  console.log('🔍 ClientDetail - Hook result:', { rawClient, loading, error });
  
  // Adapter les données API vers le format attendu par l'interface
  const client = rawClient ? {
    ...rawClient,
    name: `${rawClient.firstName} ${rawClient.lastName}`,
    type: rawClient.type || 'Particulier',
    date: rawClient.createdAt,
    services: rawClient.statistics?.totalServices || 0,
    reports: rawClient.statistics?.totalReports || 0,
    status: rawClient.isActive ? 'Actif' : 'Suspendu',
    avatar: `${(rawClient.firstName || '').charAt(0)}${(rawClient.lastName || '').charAt(0)}`.toUpperCase(),
    avatarColor: '#3B82F6',
    address: rawClient.address || 'Adresse non renseignée',
    birthDate: rawClient.dateOfBirth,
    joinDate: rawClient.createdAt,
    clientId: `C${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}` // ID court simulé
  } : null;
  
  // États pour les modales et formulaires
  const [activeTab, setActiveTab] = useState('informations');
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false });
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [employeeForm, setEmployeeForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'Administration'
  });

  if (loading) {
    return (
      <div className="client-detail-container">
        <div className="loading">
          <h2>Chargement du client...</h2>
        </div>
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="client-detail-container">
        <div className="not-found">
          <h2>Client non trouvé</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/clients')} className="btn btn-primary">
            Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  // Gestion des employés
  const handleEmployeeFormChange = (value, field) => {
    setEmployeeForm(prev => ({ ...prev, [field]: value }));
  };

  const handleAddEmployee = () => {
    setEditingEmployee(null);
    setEmployeeForm({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      role: 'Administration'
    });
    setShowEmployeeModal(true);
  };

  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee);
    setEmployeeForm({ ...employee });
    setShowEmployeeModal(true);
  };

  const handleSaveEmployee = async () => {
    try {
      if (editingEmployee) {
        await updateEmployee(editingEmployee.id, employeeForm);
      } else {
        await addEmployee(employeeForm);
      }
      setShowEmployeeModal(false);
      setEmployeeForm({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        role: 'Administration'
      });
    } catch (error) {
      console.error('Erreur sauvegarde employé:', error);
    }
  };

  const handleDeleteEmployee = (employeeId, employeeName) => {
    setConfirmModal({
      isOpen: true,
      title: 'Supprimer l\'employé',
      message: `Êtes-vous sûr de vouloir supprimer l'employé "${employeeName}" ?`,
      type: 'danger',
      onConfirm: async () => {
        try {
          await deleteEmployee(employeeId);
          setConfirmModal({ isOpen: false });
        } catch (error) {
          console.error('Erreur suppression employé:', error);
        }
      }
    });
  };

  // Actions client
  const handleResetPassword = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Réinitialiser le mot de passe',
      message: `Êtes-vous sûr de vouloir réinitialiser le mot de passe de "${client.name}" ?`,
      type: 'warning',
      onConfirm: () => {
        console.log('Réinitialisation mot de passe pour:', client.id);
        setConfirmModal({ isOpen: false });
      }
    });
  };

  const handleDeleteClient = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Supprimer le client',
      message: `Êtes-vous sûr de vouloir supprimer le client "${client.name}" ? Cette action est irréversible.`,
      type: 'danger',
      onConfirm: () => {
        console.log('Suppression client:', client.id);
        navigate('/clients');
      }
    });
  };

  const handleToggleStatus = () => {
    const newStatus = client.status === 'Actif' ? 'Suspendu' : 'Actif';
    const action = client.status === 'Actif' ? 'suspendre' : 'réactiver';
    
    setConfirmModal({
      isOpen: true,
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} le compte`,
      message: `Êtes-vous sûr de vouloir ${action} le compte de "${client.name}" ?`,
      type: client.status === 'Actif' ? 'warning' : 'success',
      onConfirm: () => {
        console.log(`${action} client:`, client.id);
        setConfirmModal({ isOpen: false });
      }
    });
  };

  const handleSendMessage = () => {
    setShowMessageModal(true);
  };

  // Contenu des onglets
  const renderTabContent = () => {
    switch (activeTab) {
      case 'informations':
        return (
          <div className="tab-content">
            <div className="client-info-section">
              <div className="client-profile-card">
                <div className="client-main-info">
                  <div className="client-avatar-info">
                    <div 
                      className="avatar-circle medium" 
                      style={{ backgroundColor: client.avatarColor }}
                    >
                      {client.avatar}
                    </div>
                    <div className="client-details">
                      <h3 className="client-full-name">{client.name}</h3>
                    </div>
                  </div>
                  <div className="account-type-info">
                    <span className="type-label-small">Type de compte</span>
                    <span className={`account-type-small ${client.type.toLowerCase()}`}>
                      {client.type}
                    </span>
                  </div>
                </div>

                <div className="client-contact-info">
                  <div className="contact-row">
                    <div className="contact-item">
                      <FaEnvelope className="contact-icon" />
                      <span>{client.email}</span>
                    </div>
                    <div className="stats-group">
                      <div className="stat-item-inline">
                        <div className="stat-value-inline">{client.services}</div>
                        <div className="stat-label-inline">Service demandés</div>
                      </div>
                      <div className="stat-item-inline">
                        <div className="stat-value-inline">{client.reports}</div>
                        <div className="stat-label-inline">Signalements reçus</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="contact-row">
                    <div className="contact-item">
                      <span className="phone-icon">📞</span>
                      <span>{client.phone}</span>
                    </div>
                  </div>
                  
                  <div className="contact-row">
                    <div className="contact-item">
                      <span className="date-icon">📅</span>
                      <span>{new Date(client.date).toLocaleDateString('fr-FR')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'activite':
        return (
          <div className="tab-content">
            <div className="activity-section">
              <div className="activity-filters">
                <div className="date-filters">
                  <span>De</span>
                  <input type="date" className="date-filter-input" placeholder="jj/mm/aaaa" />
                  <span>À</span>
                  <input type="date" className="date-filter-input" placeholder="jj/mm/aaaa" />
                </div>
                <div className="activity-tabs">
                  <button className="activity-tab-btn active">Tous</button>
                  <button className="activity-tab-btn">Récent</button>
                  <button className="activity-tab-btn">Ancien</button>
                </div>
              </div>

              <div className="activity-timeline">
                {activitiesLoading ? (
                  <div className="loading">Chargement de l'activité...</div>
                ) : activities.length === 0 ? (
                  <div className="no-data">Aucune activité récente</div>
                ) : (
                  activities.map((activity, index) => {
                    const activityDate = new Date(activity.created_at);
                    const timeAgo = getTimeAgo(activityDate);
                    const prestataireInitials = activity.prestataire_name 
                      ? activity.prestataire_name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                      : 'W';
                    
                    return (
                      <div key={activity.id || index} className="activity-item">
                        <div className="activity-avatar service-avatar">{prestataireInitials}</div>
                        <div className="activity-content">
                          <div className="activity-text">
                            {activity.prestataire_name && (
                              <>
                                Mission <strong>{activity.service_name}</strong> avec <strong>{activity.prestataire_name}</strong>
                                {activity.total_amount > 0 && ` - ${activity.total_amount.toLocaleString()} CFA`}
                              </>
                            )}
                            {!activity.prestataire_name && (
                              <>Mission <strong>{activity.service_name}</strong> - Statut: {activity.status}</>
                            )}
                          </div>
                          <div className="activity-time">{timeAgo}</div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        );

      case 'demande':
        return (
          <div className="tab-content">
            <div className="requests-section">
              <div className="section-header">
                <h3>Détails signalement</h3>
                <div className="requests-search">
                  <FaSearch className="search-icon" />
                  <input 
                    type="text" 
                    placeholder="Recherche nom, email, tel ..." 
                    className="search-input"
                  />
                </div>
              </div>

              <div className="requests-table">
                <table>
                  <thead>
                    <tr>
                      <th><input type="checkbox" /></th>
                      <th>Nom de la demande <FaSort /></th>
                      <th>Type de livraison <FaSort /></th>
                      <th>Date de début <FaSort /></th>
                      <th>Heure <FaSort /></th>
                      <th>Mode paiement <FaSort /></th>
                      <th>Montant <FaSort /></th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {requestsLoading ? (
                      <tr>
                        <td colSpan="8" className="loading">Chargement des demandes...</td>
                      </tr>
                    ) : requests.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="no-data">Aucune demande</td>
                      </tr>
                    ) : (
                      requests.map((request) => {
                        const requestDate = request.scheduled_at ? new Date(request.scheduled_at) : new Date(request.created_at);
                        return (
                          <tr key={request.id}>
                            <td><input type="checkbox" /></td>
                            <td>{request.title || 'Demande de service'}</td>
                            <td>
                              <span className={`delivery-type ${request.assignment_status === 'assigned' ? 'standard' : 'recurring'}`}>
                                {request.assignment_status === 'assigned' ? 'Assignée' : 'En attente'}
                              </span>
                            </td>
                            <td>{requestDate.toLocaleDateString('fr-FR')}</td>
                            <td>{requestDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</td>
                            <td>-</td>
                            <td>{request.estimated_price ? `${request.estimated_price.toLocaleString()} FCFA` : '-'}</td>
                            <td>
                              <div className="request-actions">
                                <FaEye className="action-icon view" title="Voir" onClick={() => navigate(`/missions/${request.id}`)} />
                                <FaTrash className="action-icon delete" title="Supprimer" />
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );

      case 'signalements':
        return (
          <div className="tab-content">
            {signalementsLoading ? (
              <div className="loading-section">Chargement des signalements...</div>
            ) : (
              <div className="reports-section">
                <div className="reports-stats">
                  <div className="stats-grid">
                    <div className="stat-card total">
                      <div className="stat-title">Nbr total de signalement</div>
                      <div className="stat-value-large">{signalementsStats.total}</div>
                    </div>
                    <div className="stat-card gravity">
                      <div className="stat-title">Nbr total de signalement par gravité</div>
                      <div className="gravity-stats">
                        <div className="gravity-item faible">
                          <span className="gravity-label">Faible</span>
                          <span className="gravity-value">{String(signalementsStats.byGravity.faible).padStart(2, '0')}</span>
                        </div>
                        <div className="gravity-item moyenne">
                          <span className="gravity-label">Moyenne</span>
                          <span className="gravity-value">{String(signalementsStats.byGravity.moyenne).padStart(2, '0')}</span>
                        </div>
                        <div className="gravity-item critique">
                          <span className="gravity-label">Critique</span>
                          <span className="gravity-value">{String(signalementsStats.byGravity.critique).padStart(2, '0')}</span>
                        </div>
                      </div>
                    </div>
                    <div className="stat-card services">
                      <div className="stat-title">Nbr total de signalement par service</div>
                      <div className="service-stats">
                        {Object.entries(signalementsStats.byService).length > 0 ? (
                          Object.entries(signalementsStats.byService).map(([service, count]) => (
                            <div className="service-item" key={service}>
                              <span className="service-label">{service}</span>
                              <span className="service-badge">{String(count).padStart(2, '0')}</span>
                            </div>
                          ))
                        ) : (
                          <div className="no-data">Aucun signalement par service</div>
                        )}
                      </div>
                      <div className="action-buttons">
                        <button className="action-btn-sm primary">Envoyer avertissement</button>
                        <button className="action-btn-sm secondary">Suspendre</button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="reports-table-section">
                  <div className="reports-header">
                    <h3>Signalements</h3>
                    <div className="reports-controls">
                      <div className="date-filter">
                        <span>Date</span>
                        <input type="date" className="date-input" placeholder="jj/mm/aaaa" />
                      </div>
                      <div className="reports-tabs">
                        <button className="reports-tab active">Tous</button>
                        <button className="reports-tab">Traité</button>
                        <button className="reports-tab">Encours</button>
                        <button className="reports-tab">Clôturé</button>
                      </div>
                      <button className="export-csv-btn">
                        📤 Exporter en CSV
                      </button>
                    </div>
                  </div>

                  <div className="filter-tabs">
                    <button className="filter-tab active">Tous</button>
                    {Object.keys(signalementsStats.byService).map(service => (
                      <button className="filter-tab" key={service}>{service}</button>
                    ))}
                  </div>

                  <div className="reports-table">
                    <table>
                      <thead>
                        <tr>
                          <th><input type="checkbox" /></th>
                          <th>Id <FaSort /></th>
                          <th>Date <FaSort /></th>
                          <th>Prestataire ayant signalé <FaSort /></th>
                          <th>Id</th>
                          <th>Service concerné <FaSort /></th>
                          <th>Motif <FaSort /></th>
                          <th>Gravité <FaSort /></th>
                          <th>Statut <FaSort /></th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {signalements.length > 0 ? (
                          signalements.map((signalement, index) => {
                            const reporterName = signalement.reporter_name || 'Inconnu';
                            const reporterInitials = reporterName.split(' ').map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2);
                            const gravity = (signalement.severity || signalement.gravity || 'faible').toLowerCase();
                            const status = (signalement.status || 'pending').toLowerCase();
                            const statusLabel = status === 'resolved' ? 'Traité' : status === 'closed' ? 'Clôturé' : 'En cours';
                            const statusClass = status === 'resolved' ? 'traite' : status === 'closed' ? 'cloture' : 'encours';

                            return (
                              <tr key={signalement.id || index}>
                                <td><input type="checkbox" /></td>
                                <td>{signalement.id?.slice(-4) || index + 1}</td>
                                <td>{signalement.created_at ? new Date(signalement.created_at).toLocaleDateString('fr-FR') : '-'}</td>
                                <td>
                                  <div className="provider-info">
                                    <div className="provider-avatar">{reporterInitials}</div>
                                    <span>{reporterName}</span>
                                  </div>
                                </td>
                                <td>#{signalement.reference || index + 1}</td>
                                <td>{signalement.service_type || signalement.service || 'N/A'}</td>
                                <td>{signalement.reason || signalement.motif || 'Non spécifié'}</td>
                                <td><span className={`gravity-badge ${gravity}`}>{gravity.charAt(0).toUpperCase() + gravity.slice(1)}</span></td>
                                <td><span className={`status-badge ${statusClass}`}>{statusLabel}</span></td>
                                <td>
                                  <div className="report-actions">
                                    <button className="action-link">Détail</button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="10" className="no-data-cell">Aucun signalement trouvé</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 'paiements':
        return (
          <div className="tab-content">
            <div className="payments-section">
              <div className="payments-header">
                <h3>Historiques des paiements</h3>
                <div className="payments-controls">
                  <div className="date-filter">
                    <span>Date</span>
                    <input type="date" className="date-input" placeholder="jj/mm/aaaa" />
                  </div>
                  <div className="payments-tabs">
                    <button className="payments-tab active">Tous</button>
                    <button className="payments-tab">Réussi</button>
                    <button className="payments-tab">Échoué</button>
                    <button className="payments-tab">Litige</button>
                  </div>
                  <button className="export-csv-btn">
                    📤 Exporter en CSV
                  </button>
                </div>
              </div>

              <div className="service-filter-tabs">
                <button className="service-filter-tab active">Tous</button>
                <button className="service-filter-tab">Transport</button>
                <button className="service-filter-tab">Livraison</button>
                <button className="service-filter-tab">Location BTP</button>
                <button className="service-filter-tab">Conducteur</button>
              </div>

              <div className="payments-table">
                <table>
                  <thead>
                    <tr>
                      <th><input type="checkbox" /></th>
                      <th>REF <FaSort /></th>
                      <th>Date du paiements <FaSort /></th>
                      <th>Type <FaSort /></th>
                      <th>Mission/Service</th>
                      <th>Montant TTC <FaSort /></th>
                      <th>Mode de paiement <FaSort /></th>
                      <th>Statut <FaSort /></th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><input type="checkbox" /></td>
                      <td>TRX1024</td>
                      <td>11/07/2025</td>
                      <td>Paiement mission</td>
                      <td>
                        <div className="mission-info">
                          Transport (ID#4451)
                        </div>
                      </td>
                      <td>125 CFA</td>
                      <td>Portefeuille</td>
                      <td><span className="payment-status-badge reussi">Réussi</span></td>
                      <td>
                        <div className="payment-actions">
                          <button className="action-link">Détail</button>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td><input type="checkbox" /></td>
                      <td>TRX1024</td>
                      <td>5/07/2025</td>
                      <td>Paiement mission</td>
                      <td>
                        <div className="mission-info">
                          Transport (ID#4451)
                        </div>
                      </td>
                      <td>128 CFA</td>
                      <td>Cash</td>
                      <td><span className="payment-status-badge litige">Litige</span></td>
                      <td>
                        <div className="payment-actions">
                          <button className="action-link">Détail</button>
                          <button className="action-btn-mini">Clôturé</button>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td><input type="checkbox" /></td>
                      <td>TRX1024</td>
                      <td>12/04/2025</td>
                      <td>Paiement mission</td>
                      <td>
                        <div className="mission-info">
                          Transport (ID#4451)
                        </div>
                      </td>
                      <td>30 CFA</td>
                      <td>Carte bancaire</td>
                      <td><span className="payment-status-badge echoue">Échoué</span></td>
                      <td>
                        <div className="payment-actions">
                          <button className="action-link">Détail</button>
                          <button className="action-btn-mini primary">Relancer</button>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td><input type="checkbox" /></td>
                      <td>TRX1024</td>
                      <td>7/18/2025</td>
                      <td>Remboursement</td>
                      <td>
                        <div className="mission-info">
                          Transport (ID#4451)
                        </div>
                      </td>
                      <td>-53 CFA</td>
                      <td>Mobile money</td>
                      <td><span className="payment-status-badge litige">Litige</span></td>
                      <td>
                        <div className="payment-actions">
                          <button className="action-link">Forcer</button>
                          <button className="action-link">Suspendre</button>
                          <button className="action-link">Détail</button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );

      case 'evaluation':
        return (
          <div className="tab-content">
            <div className="evaluations-section">
              <h3>Evaluation par les prestataires</h3>
              
              <div className="evaluations-grid">
                {evaluationsLoading ? (
                  <div className="loading">Chargement des évaluations...</div>
                ) : evaluations.length === 0 ? (
                  <div className="no-data">Aucune évaluation</div>
                ) : (
                  evaluations.map((evaluation) => {
                    const evaluationDate = new Date(evaluation.created_at);
                    const providerInitials = evaluation.prestataire?.name 
                      ? evaluation.prestataire.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                      : 'P';
                    const rating = Math.floor(evaluation.rating || 0);
                    
                    return (
                      <div key={evaluation.id} className="evaluation-card">
                        <div className="evaluation-header">
                          <div className="provider-logo">{providerInitials}</div>
                          <div className="provider-name">{evaluation.prestataire?.name || 'Prestataire'}</div>
                        </div>
                        <div className="evaluation-rating">
                          {[1, 2, 3, 4, 5].map(star => (
                            <FaStar 
                              key={star} 
                              className={star <= rating ? "star filled" : "star empty"} 
                            />
                          ))}
                        </div>
                        <div className="evaluation-date">{evaluationDate.toLocaleDateString('fr-FR')}</div>
                        <div className="evaluation-comment">
                          {evaluation.comment || 'Aucun commentaire'}
                        </div>
                        {evaluation.mission && (
                          <div className="evaluation-mission">
                            Mission: {evaluation.mission.service_type} - {evaluation.mission.amount.toLocaleString()} CFA
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        );

      case 'employe':
        return (
          <div className="tab-content">
            <div className="employees-section">
              <div className="employees-header">
                <div className="search-container">
                  <input 
                    type="text" 
                    placeholder="Recherche nom, email, tel ..." 
                    className="search-input"
                  />
                </div>
                <button 
                  className="btn btn-primary add-employee-btn"
                  onClick={handleAddEmployee}
                >
                  <FaPlus /> Ajouter un employé
                </button>
              </div>

              <div className="employees-table">
                <table>
                  <thead>
                    <tr>
                      <th><input type="checkbox" /></th>
                      <th>Nom et prénom de l'employés</th>
                      <th>Téléphone</th>
                      <th>Rôle</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map((employee, index) => (
                      <tr key={employee.id || index}>
                        <td><input type="checkbox" /></td>
                        <td>
                          <div className="employee-info">
                            <div className="employee-avatar">
                              {employee.firstName?.charAt(0)}{employee.lastName?.charAt(0)}
                            </div>
                            <span>{employee.firstName} {employee.lastName}</span>
                          </div>
                        </td>
                        <td>{employee.phone}</td>
                        <td>{employee.role}</td>
                        <td>
                          <div className="actions">
                            <FaTrash 
                              className="action-icon delete"
                              onClick={() => handleDeleteEmployee(employee.id, `${employee.firstName} ${employee.lastName}`)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                    {employees.length === 0 && (
                      <tr>
                        <td colSpan="5" className="no-data">
                          Aucun employé trouvé
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );

      default:
        return <div className="tab-content">Contenu non disponible</div>;
    }
  };

  return (
    <div className="client-detail-page-wrapper-figma-isolated">
      {/* Header avec actions */}
      <div className="figma-client-header-isolated">
        <div className="figma-header-left-isolated">
          <button className="figma-back-btn-isolated" onClick={() => navigate('/clients')}>
            <FaArrowLeft /> Fiche détails clients
          </button>
        </div>
        <div className="figma-header-actions-isolated">
          <button className="figma-action-btn-isolated figma-secondary-isolated" onClick={handleResetPassword}>
            Réinitialiser mot de passe
          </button>
          <button className="figma-action-btn-isolated figma-danger-isolated" onClick={handleDeleteClient}>
            Supprimer
          </button>
          <button className="figma-action-btn-isolated figma-success-isolated" onClick={handleToggleStatus}>
            {client.status === 'Actif' ? 'Suspendre' : 'Réactiver le compte'}
          </button>
          <button className="figma-action-btn-isolated figma-primary-isolated" onClick={handleSendMessage}>
            Message
          </button>
          <button className="figma-action-btn-isolated figma-secondary-isolated" onClick={handleToggleStatus}>
            {client.status === 'Actif' ? 'Suspendre' : 'Suspendre'}
          </button>
        </div>
      </div>

      {/* Contenu original non modifié */}
      <div className="client-detail-page">

      {/* Section profil client */}
      <div className="client-profile-section">
        <div className="client-profile">
          <div className="client-avatar">
            <div 
              className="avatar-circle large" 
              style={{ backgroundColor: client.avatarColor }}
            >
              {client.avatar}
            </div>
          </div>
          <div className="client-info">
            <h2 className="client-name">{client.name}</h2>
            <span className="client-id">{client.clientId}</span>
          </div>
          <div className="client-badges">
            <span className="badge success">Document validé</span>
          </div>
          <div className="client-type">
            <span className="type-label">Type de compte</span>
            <span className={`account-type-badge ${client.type.toLowerCase()}`}>
              {client.type}
            </span>
          </div>
        </div>
      </div>

      {/* Navigation et contenu */}
      <div className="client-content">
        <div className="tabs-navigation">
          <nav className="vertical-tabs">
            {[
              { key: 'informations', label: 'Informations personnelles', icon: <FaUser /> },
              { key: 'activite', label: 'Activité', icon: <FaHistory /> },
              { key: 'demande', label: 'Demande', icon: <FaClipboardList /> },
              { key: 'signalements', label: 'Signalements', icon: <FaExclamationTriangle /> },
              { key: 'paiements', label: 'Paiements', icon: <FaMoneyBillWave /> },
              { key: 'evaluation', label: 'Evaluation', icon: <FaStar /> },
              { key: 'employe', label: 'Employé', icon: <FaBriefcase />, highlight: true }
            ].map(tab => (
              <button
                key={tab.key}
                className={`tab-btn ${activeTab === tab.key ? 'active' : ''} ${tab.highlight ? 'highlight' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                <span className="tab-icon">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="tab-content-area">
          {renderTabContent()}
        </div>
      </div>

      {/* Modales */}
      <Modal
        isOpen={showEmployeeModal}
        onClose={() => setShowEmployeeModal(false)}
        title={editingEmployee ? 'Modifier l\'employé' : 'Ajouter un employé'}
        size="medium"
      >
        <div className="employee-form">
          <FormField
            label="Prénom"
            type="text"
            value={employeeForm.firstName}
            onChange={(value) => handleEmployeeFormChange(value, 'firstName')}
            required
          />
          <FormField
            label="Nom"
            type="text"
            value={employeeForm.lastName}
            onChange={(value) => handleEmployeeFormChange(value, 'lastName')}
            required
          />
          <FormField
            label="Email"
            type="email"
            value={employeeForm.email}
            onChange={(value) => handleEmployeeFormChange(value, 'email')}
            required
          />
          <FormField
            label="Téléphone"
            type="tel"
            value={employeeForm.phone}
            onChange={(value) => handleEmployeeFormChange(value, 'phone')}
            required
          />
          <FormField
            label="Rôle"
            type="select"
            value={employeeForm.role}
            onChange={(value) => handleEmployeeFormChange(value, 'role')}
            options={[
              { value: 'Administration', label: 'Administration' },
              { value: 'Trésorier', label: 'Trésorier' },
              { value: 'Manager', label: 'Manager' },
              { value: 'Employé', label: 'Employé' }
            ]}
            required
          />
          <div className="form-actions">
            <button 
              className="btn btn-secondary" 
              onClick={() => setShowEmployeeModal(false)}
            >
              Annuler
            </button>
            <button 
              className="btn btn-primary" 
              onClick={handleSaveEmployee}
            >
              {editingEmployee ? 'Modifier' : 'Ajouter'}
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
      />

      <MessageModal
        isOpen={showMessageModal}
        onClose={() => setShowMessageModal(false)}
        client={client}
        type="message"
      />
      </div>
    </div>
  );
};

export default ClientDetail;